import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook } from "@fortawesome/free-brands-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import UseMegaMenu from "./useMegaMenu";
import { useNavigate } from "react-router-dom";

// ============================================================
// TYPES
// ============================================================

interface SubCategory {
  id: number;
  name: string;
  url?: string;
}

interface MenuCategory {
  id: number;
  name: string;
  subCategories: SubCategory[];
}

interface Promotion {
  title: string;
  description: string;
  buttonText: string;
  image: string;
  backgroundColor?: string;
}

const STATIC_PROMOTION: Promotion = {
  title: "New Collection",
  description: "Up to 30% OFF",
  buttonText: "Shop Now",
  image: "/workingMan.svg",
  backgroundColor: "#6A0DAD",
};

interface MegaMenuItem {
  CustomerMenuMasterID: number;
  CustomerMenuName: string;
  MenuTypeMasterID: number;
  MenuURL: string | null;
  //Departments: CustomerDepartmentResponse[];

  promotion?: Promotion;
}

// ============================================================
// MENU DATA
// ============================================================

interface MegaMenuProps {
  mobileDrawerOpen: boolean;
  onMobileDrawerClose: () => void;
}

// ============================================================
// COMPONENT
// ============================================================

const MegaMenu: React.FC<MegaMenuProps> = ({
  mobileDrawerOpen,
  onMobileDrawerClose,
}) => {

  const { loading,
    isUpdate,
    selectMegaMenuList, } = UseMegaMenu();
const navigate = useNavigate();
  // ============================================================
  // DESKTOP STATE
  // ============================================================

  const [activeMenu, setActiveMenu] =
    useState<number | null>(null);

  // ============================================================
  // MOBILE STATE
  // ============================================================

  // const [mobileDrawerOpen, setMobileDrawerOpen] =
  //   useState<boolean>(false);

  const [mobileActiveMenu, setMobileActiveMenu] =
    useState<number | null>(null);

  const [mobileActiveCategoryGroup, setMobileActiveCategoryGroup] =
    useState<number | null>(null);

  // ============================================================
  // MEGA MENU POSITION
  // ============================================================

  const desktopMenuRef =
    useRef<HTMLDivElement | null>(null);

  const [megaMenuLeft, setMegaMenuLeft] =
    useState<number>(0);

  const [megaMenuWidth, setMegaMenuWidth] =
    useState<number>(0);

  // ============================================================
  // ACTIVE MENU DATA
  // ============================================================

const activeMenuData = selectMegaMenuList.find(
  (menu) =>
    menu.CustomerMenuMasterID === activeMenu
);

  // ============================================================
  // CALCULATE FULL WIDTH MEGA MENU
  // ============================================================

  const updateMegaMenuPosition = () => {
    if (!desktopMenuRef.current) {
      return;
    }

    const rect =
      desktopMenuRef.current.getBoundingClientRect();

    /*
     * Example:
     *
     * Main menu starts at 400px
     *
     * megaMenuLeft = -400
     *
     * So the absolute mega menu moves
     * back to browser left edge.
     */

    setMegaMenuLeft(-rect.left);

    setMegaMenuWidth(window.innerWidth);
  };

  // ============================================================
  // UPDATE POSITION ON RESIZE
  // ============================================================

  useEffect(() => {
    if (activeMenu === null) {
      return;
    }

    updateMegaMenuPosition();

    const handleResize = () => {
      updateMegaMenuPosition();
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, [activeMenu]);

  // ============================================================
  // DESKTOP MENU ENTER
  // ============================================================

  const handleDesktopMenuEnter = (
    menuId: number
  ) => {
    setActiveMenu(menuId);

    /*
     * Wait for browser layout before
     * calculating the mega menu position.
     */
    requestAnimationFrame(() => {
      updateMegaMenuPosition();
    });
  };

  // ============================================================
  // DESKTOP MENU CLICK
  // ============================================================

  const handleDesktopMenuClick = (
    menu: MegaMenuItem
  ) => {
    if (menu.MenuTypeMasterID === 2) {
      if (menu.MenuURL) {
        navigate(menu.MenuURL);
        setActiveMenu(null);
      }
      return;
    }

    setActiveMenu((previous) => {
      const next =
        previous === menu.CustomerMenuMasterID
          ? null
          : menu.CustomerMenuMasterID;

      if (next !== null) {
        requestAnimationFrame(() => {
          updateMegaMenuPosition();
        });
      }

      return next;
    });
  };

  const handlePlainMenuClick = (menu: MegaMenuItem) => {
    if (menu.MenuTypeMasterID !== 2) {
      return;
    }

    if (menu.MenuURL) {
      navigate(menu.MenuURL);
      setActiveMenu(null);
    }
  };

  // ============================================================
  // DESKTOP MOUSE LEAVE
  // ============================================================

  const handleDesktopMenuLeave = () => {
    setActiveMenu(null);
  };

  // ============================================================
  // MOBILE
  // ============================================================

  const handleMobileMenuClick = (
    menuId: number
  ) => {
    setMobileActiveMenu((previous) =>
      previous === menuId
        ? null
        : menuId
    );

    setMobileActiveCategoryGroup(null);
  };

  const handleCloseMobileDrawer = () => {
    onMobileDrawerClose();
    setMobileActiveMenu(null);
    setMobileActiveCategoryGroup(null);
  };

  const handleMobileCategoryGroupClick = (
    categoryGroupId: number
  ) => {
    setMobileActiveCategoryGroup((previous) =>
      previous === categoryGroupId
        ? null
        : categoryGroupId
    );
  };

  const handleShopClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    setActiveMenu(null);
    handleCloseMobileDrawer();
    navigate("/shop");
  };

  const handleCategoryClick = (productCategoryMasterID: number) => {
    console.log("[MegaMenu] Category clicked:", {
      productCategoryMasterID,
    });

    if (productCategoryMasterID) {
      navigate("/shop", {
        state: {
          ProductCategoryMasterID: productCategoryMasterID,
        },
      });
    }
    setActiveMenu(null);
    handleCloseMobileDrawer();
  };

  // ============================================================
  // RETURN
  // ============================================================

  return (
    <>
      {/* ========================================================
          DESKTOP
      ======================================================== */}

      <Box
        ref={desktopMenuRef}
        sx={{
          display: {
            xs: "none",
            md: "block",
          },
          /*
           * IMPORTANT:
           *
           * This wrapper is FULL WIDTH.
           *
           * Your main menu can still be
           * positioned however you want.
           */
          width: "100%",
          position: "relative",
          /*
           * Do NOT use overflow:hidden here.
           */
          overflow: "visible",
        }}

        onMouseLeave={
          handleDesktopMenuLeave
        }
      >
        {/* =====================================================
            MAIN NAVIGATION
        ===================================================== */}

        <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: {md: 3, lg: 4,},
            height: "50px",
            /*
             * KEEP YOUR MAIN MENU POSITION HERE.
             *
             * You can change this without affecting
             * mega menu width.
             */
          }}
        >
          {selectMegaMenuList.map(
            (menu) => {
              const isDropdownMenu = menu.MenuTypeMasterID === 1;

              return (
                <Box
                  key={menu.CustomerMenuMasterID}
                  onMouseEnter={() => {
                    if (isDropdownMenu) {
                      handleDesktopMenuEnter(
                        menu.CustomerMenuMasterID
                      );
                      return;
                    }

                    setActiveMenu(null);
                  }}
                  onClick={() => handleDesktopMenuClick(menu)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    position: "relative",
                    userSelect: "none",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#ffffff",
                      fontSize: {
                        md: "14px",
                        lg: "15px",
                      },
                      fontWeight: 300,
                      whiteSpace: "nowrap",
                      "&:hover": { color: "#c453e7" },
                    }}
                  >
                    {menu.CustomerMenuName}
                  </Typography>

                  {isDropdownMenu && (
                    <KeyboardArrowDownIcon
                      sx={{
                        ml: 0.3,
                        color: "#FFFFFF",
                        fontSize: 20,
                        transition: "transform 0.2s ease",
                        transform:
                          activeMenu === menu.CustomerMenuMasterID
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                      }}
                    />
                  )}
                </Box>
              );
            }
          )}
        </Box>

        {/* =====================================================
            FULL WIDTH MEGA MENU
        ===================================================== */}

        {activeMenuData && (
          <Box
            key={activeMenu}
            sx={{
              position:
                "absolute",
              top: "50px",
              /*
               * THIS IS THE IMPORTANT CHANGE
               *
               * If the desktop wrapper starts at
               * 400px from browser left:
               *
               * left = -400px
               *
               * Therefore submenu starts at
               * browser left = 0px.
               */
              left:
                `${megaMenuLeft}px`,
              /*
               * Always viewport width.
               */
              width:
                `${megaMenuWidth}px`,
              maxWidth:
                "100vw",
              backgroundColor:
                "#000000",
              //   border: "1px solid transparent",
              // background:
              //   "linear-gradient(#0A0A0B, #0A0A0B) padding-box, radial-gradient(circle at top center, #fff -10%, #373737 30%) border-box",

              boxShadow:
                "0 12px 30px rgba(0,0,0,0.12)",

              zIndex:
                99999,
            }}
          >
            {/* =================================================
                INNER CONTENT
            ================================================= */}

            <Box
              sx={{
                width:
                  "100%",
                maxWidth:
                  "1400px",
                mx: "auto",
                px: {
                  md: 3,
                  lg: 5,
                },
                py: {
                  md: 3,
                  lg: 4,
                },
                display:
                  "grid",
                gridTemplateColumns:
                {
                  md:
                    "1fr 230px",
                  lg:
                    "1fr 260px",
                },
                gap: {
                  md: 3,
                  lg: 4,
                },
              }}
            >
              {/* =================================================
                  CATEGORY AREA
              ================================================= */}

              <Box
                sx={{
                  display:
                    "grid",
                  gridTemplateColumns:
                  {
                    md:
                      "repeat(3, minmax(0, 1fr))",
                    lg:
                      "repeat(4, minmax(0, 1fr))",
                  },
                  columnGap: {
                    md: 3,
                    lg: 4,
                  },
                  rowGap: 4,
                }}
              >
                {(activeMenuData.Departments || []).map((department:any) => (
  <Box
    key={`${activeMenu}-${department.CustomerDepartmentMasterID}`}
    sx={{
      width: "100%",
    }}
  >
    {/* DEPARTMENT */}

    <Typography
      sx={{
        fontSize: {
          md: "12px",
          lg: "12px",
        },
        fontWeight: 200,
        color: "#FFFFFF",
      }}
    >
      {department.CustomerDepartmentName}
    </Typography>

    {/* CATEGORY GROUPS */}

    {(department.CategoryGroups || []).map((categoryGroup:any) => (
      <Box
        key={`${activeMenu}-${categoryGroup.CustomerCategoryGroupMasterID}`}
        sx={{
          minWidth: 0,
          mb: 3,
        }}
      >
        {/* CATEGORY GROUP */}
        <Typography
          sx={{
            fontSize: {
              md: "14px",
              lg: "15px",
            },
            fontWeight: 600,
            color: "#ffffff",
            mb: 1,
          }}
        >
          {categoryGroup.CustomerCategoryGroupName}
        </Typography>

                          {/* CATEGORIES */}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {(categoryGroup.Categories || []).map((category:any) => (
              <Box
                key={`${activeMenu}-${category.ProductCategoryMasterID}`}
              >
                {/* CATEGORY */}

                <Typography
                  sx={{
                    fontSize: {
                      md: "13px",
                      lg: "14px",
                    },
                    lineHeight: 1,
                    fontWeight: 300,
                    color: "#AAAAAA",
                    cursor: "pointer",
                    transition:
                      "all 0.2s ease",
                    "&:hover": {
                      color: "#FFFFFF",
                      transform:
                        "translateX(4px)",
                    },
                  }}
                  onClick={() =>
                    handleCategoryClick(category.ProductCategoryMasterID)
                  }
                >
                  {category.ProductCategoryName}
                </Typography>

                {/* SUBCATEGORIES */}
                {/* if you want to add subcategroy in mega menu thne uncomment it */}
                {/* {category.SubCategories.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      ml: 1.5,
                      mt: 0.5,
                    }}
                  >
                    {category.SubCategories.map((subCategory:any) => (
                        <Box
                          key={
                            subCategory.ProductSubCategoryMasterID
                          }
                          component="a"
                          href={`/products?subCategoryId=${subCategory.ProductSubCategoryMasterID}`}
                          onClick={(event) => {
                            event.stopPropagation();

                            setActiveMenu(null);
                          }}
                          sx={{
                            display:
                              "block",
                            fontSize: {
                              md: "12px",
                              lg: "13px",
                            },
                            lineHeight:
                              1.5,
                            color:
                              "#777777",
                            textDecoration:
                              "none",
                            transition:
                              "all 0.2s ease",
                            "&:hover": {
                              color:
                                "#FFFFFF",
                              transform:
                                "translateX(4px)",
                            },
                          }}
                        >
                          {
                            subCategory.ProductSubCategoryName
                          }
                        </Box>
                      )
                    )}
                  </Box>
                )} */}
              </Box>
            )
          )}
        </Box>
      </Box>
    ))}
  </Box>
))}
              </Box>

              {/* =================================================
                  PROMOTION IMAGE
              ================================================= */}

              <Box
                sx={{
                  width:
                    "100%",
                  minHeight: {
                    md: 280,
                    lg: 320,
                  },
                  borderRadius:
                    "14px",
                  overflow:
                    "hidden",
                  color: "#FFFFFF",
                  backgroundColor:
                    STATIC_PROMOTION.backgroundColor ||
                    "#6A0DAD",
                  display:
                    "flex",
                  flexDirection:
                    "column",
                  alignItems:
                    "center",
                  textAlign:
                    "center",
                }}
              >
                <Typography
                  sx={{
                    mt: 2.5,
                    fontSize:
                      "14px",
                    color:
                      "#ffffff",
                  }}
                >
                  {STATIC_PROMOTION.title}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    px: 1,
                    fontSize: {
                      md:
                        "16px",
                      lg:
                        "18px",
                    },
                    fontWeight:
                      700,
                    color:
                      "#ffffff",
                  }}
                >
                  {STATIC_PROMOTION.description}
                </Typography>

                <Button
                  variant="contained"
                  onClick={handleShopClick}
                  sx={{
                    mt: 1.5,
                    px: 2.5,
                    py: 0.8,
                    borderRadius:
                      "30px",
                    backgroundColor:
                      "#078B5A",
                    textTransform:
                      "none",
                    boxShadow:
                      "none",
                    "&:hover":
                    {
                      backgroundColor:
                        "#067A4F",
                      boxShadow:
                        "none",
                    },
                  }}
                >
                  {STATIC_PROMOTION.buttonText}
                </Button>

                <Box
                  component="img"
                  src={STATIC_PROMOTION.image}
                  alt={
                    activeMenuData.CustomerMenuName
                  }
                  sx={{
                    width:
                      "170px",
                    mt:
                      "auto",
                    objectFit:
                      "cover",
                    objectPosition:
                      "center bottom",
                    display:
                      "block",
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* ========================================================
          MOBILE DRAWER
      ======================================================== */}

      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleCloseMobileDrawer}
        PaperProps={{
          sx: {
            width: {
              xs: "100vw",
              sm: '100%',
            },
            maxWidth: "100%",
            overflowX: "hidden",
            background: '#000000'
          },
        }}
      >
        {/* ======================================================
            DRAWER HEADER
        ====================================================== */}

        <Box
          sx={{
            height:
              "64px",

            px: 2,

            display:
              "flex",

            alignItems:
              "center",

            justifyContent:
              "space-between",

            backgroundColor:
              "#000000",
          }}
        >
          <Typography
            sx={{
              color:
                "#FFFFFF",
            }}
          >
            <img src="/logo.png" alt="Logo" style={{ height: '40px' }} />
          </Typography>

          <IconButton
            onClick={
              handleCloseMobileDrawer
            }
            sx={{
              color:
                "#FFFFFF",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ======================================================
            MOBILE MENU LIST
        ====================================================== */}

        <List
          disablePadding
          sx={{width:"100%",
            overflowY:
              "auto",
            flex: 1,
          }}
        >
          {selectMegaMenuList.map(
            (menu) => {
              const isDropdownMenu = menu.MenuTypeMasterID === 1;
              const isMenuOpen =
                mobileActiveMenu ===
                menu.CustomerMenuMasterID;

              return (
                <React.Fragment
                  key={
                    menu.CustomerMenuMasterID
                  }
                >
                  {/* MAIN MENU */}

                  <ListItemButton
                    onClick={() => {
                      if (isDropdownMenu) {
                        handleMobileMenuClick(menu.CustomerMenuMasterID);
                        return;
                      }

                      if (menu.MenuURL) {
                        navigate(menu.MenuURL);
                        handleCloseMobileDrawer();
                      }
                    }}
                    sx={{
                      minHeight:
                        54,

                      px: 2,

                      background: '#000000',

                      backgroundColor:
                        isMenuOpen
                          ? "#080808"
                          : "#000000",
                    }}
                  >
                    <ListItemText
                      primary={
                        menu.CustomerMenuName
                      }

                      primaryTypographyProps={{
                        fontSize:
                          "16px",

                        fontWeight:
                          300,

                        color:
                          "#989898",
                      }}
                    />

                    {isDropdownMenu && (
                      <KeyboardArrowRightIcon
                        sx={{
                          color:
                            "#555555",

                          transition:
                            "transform 0.2s",

                          transform:
                            isMenuOpen
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                        }}
                      />
                    )}
                  </ListItemButton>

                  <Divider sx={{ borderColor: '#161616' }} />

                  {/* MOBILE SUBMENU: CATEGORY GROUPS */}

                  {isDropdownMenu && (
                    <Collapse
                      in={
                        isMenuOpen
                      }

                      timeout="auto"

                      unmountOnExit
                    >
                      <Box
                        sx={{
                          backgroundColor:
                            "#111111",
                        }}
                      >
                      {(menu.Departments || [])
                        .flatMap(
                          (department: any) =>
                            department.CategoryGroups || []
                        )
                        .map(
                          (
                            categoryGroup: any
                          ) => {
                            const isCategoryGroupOpen =
                              mobileActiveCategoryGroup ===
                              categoryGroup.CustomerCategoryGroupMasterID;

                            return (
                              <React.Fragment
                                key={
                                  categoryGroup.CustomerCategoryGroupMasterID
                                }
                              >
                                {/* CATEGORY GROUP */}

                                <ListItemButton
                                  onClick={() =>
                                    handleMobileCategoryGroupClick(
                                      categoryGroup.CustomerCategoryGroupMasterID
                                    )
                                  }

                                  sx={{
                                    minHeight:
                                      48,

                                    pl: 3,

                                    pr: 2,
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      categoryGroup.CustomerCategoryGroupName
                                    }

                                    primaryTypographyProps={{
                                      fontSize:
                                        "15px",

                                      fontWeight:
                                        300,

                                      color:
                                        "#989898",
                                    }}
                                  />

                                  <KeyboardArrowDownIcon
                                    sx={{
                                      fontSize:
                                        20,

                                      color:
                                        "#555555",

                                      transition:
                                        "transform 0.2s",

                                      transform:
                                        isCategoryGroupOpen
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                    }}
                                  />
                                </ListItemButton>

                                {/* CATEGORIES */}

                                <Collapse
                                  in={
                                    isCategoryGroupOpen
                                  }

                                  timeout="auto"

                                  unmountOnExit
                                >
                                  <List
                                    disablePadding

                                    sx={{
                                      backgroundColor:
                                        "#000000",
                                    }}
                                  >
                                    {(categoryGroup.Categories || []).map(
                                      (
                                        category: any
                                      ) => (
                                        <ListItemButton
                                          key={
                                            category.ProductCategoryMasterID
                                          }

                                          onClick={() =>
                                            handleCategoryClick(
                                              category.ProductCategoryMasterID
                                            )
                                          }

                                          sx={{
                                            minHeight:
                                              42,

                                            pl: 5,

                                            pr: 2,
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              category.ProductCategoryName
                                            }

                                            primaryTypographyProps={{
                                              fontSize:
                                                "14px",

                                              color:
                                                "#555555",
                                            }}
                                          />
                                        </ListItemButton>
                                      )
                                    )}
                                  </List>
                                </Collapse>

                                <Divider sx={{ borderColor: '#1a1919' }} />
                              </React.Fragment>
                            );
                          }
                        )}

                      {/* MOBILE PROMOTION */}

                      <Box
                        sx={{
                          mx: 2,

                          my: 2,

                          borderRadius:
                            "14px",

                          overflow:
                            "hidden",

                          backgroundColor:
                            STATIC_PROMOTION.backgroundColor ||
                            "#6A0DAD",

                          textAlign:
                            "center",
                        }}
                      >
                        <Typography
                          sx={{
                            pt: 2,

                            fontSize:
                              "13px",

                            color:
                              "#222222",
                          }}
                        >
                          {STATIC_PROMOTION.title}
                        </Typography>

                        <Typography
                          sx={{
                            px: 1,

                            mt: 0.5,

                            fontSize:
                              "16px",

                            fontWeight:
                              700,

                            color:
                              "#111111",
                          }}
                        >
                          {STATIC_PROMOTION.description}
                        </Typography>

                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleShopClick}
                          sx={{
                            mt: 1.5,

                            mb: 1.5,

                            px: 2.5,

                            borderRadius:
                              "30px",

                            backgroundColor:
                              "#078B5A",

                            textTransform:
                              "none",

                            boxShadow:
                              "none",

                            "&:hover":
                            {
                              backgroundColor:
                                "#067A4F",

                              boxShadow:
                                "none",
                            },
                          }}
                        >
                          {STATIC_PROMOTION.buttonText}
                        </Button>

                        <Box
                          component="img"

                          src={STATIC_PROMOTION.image}

                          alt={
                            menu.CustomerMenuName
                          }

                          sx={{
                            width:
                              "170px",

                            objectFit:
                              "cover",

                            objectPosition:
                              "center bottom",

                            display:
                              "block",
                          }}
                        />
                        </Box>
                      </Box>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            }
          )}
        </List>
        <Box sx={{ bottom: "16px", position: "absolute", left: '16px', zIndex: -1, }}>
          <Typography sx={{ color: "#fff", fontSize: "14px", fontWeight: "300", marginBottom: "10px" }}>Follow us</Typography>
          <Box sx={{
            display: "flex",
            gap: "12px",
            justifyContent: "space-between",
          }}>
            {/* <Link
              href=""
              sx={{
                color: "#fff",
                textDecoration: "none",
                "&:hover": { color: "#FECC00", textDecoration: "none" },
              }}
              // target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faFacebook} style={{ fontSize: "22px" }} />
            </Link> */}

            <Link
              href="https://api.whatsapp.com/send?phone=918856042557"
              sx={{
                color: "#fff",
                textDecoration: "none",
                "&:hover": { color: "#FECC00", textDecoration: "none" },
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} style={{ fontSize: "22px" }} />
            </Link>

            <Link
              href="https://www.linkedin.com/company/theknockoutculture/"
              sx={{
                color: "#fff",
                textDecoration: "none",
                "&:hover": { color: "#FECC00", textDecoration: "none" },
              }}
               target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faLinkedin} style={{ fontSize: "22px" }} />
            </Link>

            <Link
              href="https://instagram.com/TheKnockoutCulture"
              sx={{
                color: "#fff",
                textDecoration: "none",
                "&:hover": { color: "#FECC00", textDecoration: "none" },
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon
                icon={faInstagram}
                style={{ fontSize: "22px" }}
              />
            </Link>

            <Link
              href="https://www.youtube.com/channel/UCoL4PVeYZGevMmQ4mYepuSQ"
              sx={{
                color: "#fff",
                textDecoration: "none",
                "&:hover": { color: "#FECC00", textDecoration: "none" },
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faYoutube} style={{ fontSize: "22px" }} />
            </Link>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default MegaMenu;